import { ConfigProvider, DatePicker, Input, Select } from "antd";
import _ from "lodash";
import { useAtomValue } from "jotai";
import moment from "moment";
import { styled } from "styled-components";
import {
  MdCategory,
  MdEmojiEvents,
  MdEvent,
  MdPerson,
  MdPublic,
  MdStarOutline,
} from "react-icons/md";
import { masterToOptions } from "../../../utils/functions/misc/masterToOptions";
import { masterAtom } from "../../../atoms/masters.atom";
import { colors } from "../../../theme/colors";
import { Field, FieldGrid, card } from "./section-card.component";

export const CreateAward = ({
  countryOptions,
  directors,
  producers,
  cast,
  crew,
}: {
  countryOptions: { value: string; label: string }[] | Record<string, string>[];
  directors: { name: string; user?: string; biography: string }[];
  producers: { name: string; user?: string; filmography: string }[];
  cast: { name: string; user?: string; character: string }[];
  crew: { name: string; user?: string; role: string }[];
}) => {
  const master = useAtomValue(masterAtom);

  const winnerOptions = _.uniqBy(
    [
      ...directors
        .filter((el) => el.name)
        .map((el, i) => ({ label: el.name, value: `${i}__director` })),
      ...producers
        .filter((el) => el.name)
        .map((el, i) => ({ label: el.name, value: `${i}__producer` })),
      ...cast
        .filter((el) => el.name)
        .map((el, i) => ({ label: el.name, value: `${i}__cast` })),
      ...crew
        .filter((el) => el.name)
        .map((el, i) => ({ label: el.name, value: `${i}__crew` })),
    ],
    "label"
  );

  return (
    <ModalBody>
      <FieldGrid>
        <Field
          name="name_of_festival"
          label="Festival Name"
          icon={<MdEmojiEvents />}
          required
          rules={[{ required: true, message: "Please enter festival name!" }]}
          helper="The official name of the festival or ceremony."
          fullWidth
        >
          <Input size="large" placeholder="Enter festival name" />
        </Field>

        <Field
          name="country"
          label="Country"
          icon={<MdPublic />}
          required
          rules={[{ required: true, message: "Please select award country!" }]}
          helper="Where the festival was held."
        >
          <Select
            size="large"
            placeholder="Select country"
            options={countryOptions as any}
            showSearch
            allowClear
          />
        </Field>

        <Field
          name="award_year"
          label="Year"
          icon={<MdEvent />}
          required
          rules={[{ required: true, message: "Please select award year!" }]}
          helper="The year of the ceremony."
        >
          <DatePicker
            size="large"
            placeholder="Select award year"
            picker="year"
            format="YYYY"
            style={{ width: "100%" }}
            disabledDate={(date) => date && +date > moment.now()}
          />
        </Field>

        <Field
          name="award_category"
          label="Category"
          icon={<MdCategory />}
          required
          rules={[{ required: true, message: "Please select award category!" }]}
          helper="The category the film was recognized in."
        >
          <Select
            size="large"
            placeholder="Select award category"
            options={masterToOptions("award_category", master)}
            showSearch
          />
        </Field>

        <Field
          name="award_status"
          label="Status"
          icon={<MdStarOutline />}
          required
          rules={[{ required: true, message: "Please select award status!" }]}
          helper="Did the film win, get nominated, or screen?"
        >
          <Select
            size="large"
            placeholder="Select award status"
            showSearch
            options={masterToOptions("festival_status", master)}
          />
        </Field>

        <ConfigProvider
          renderEmpty={() =>
            "Please add cast and crew details in the previous step!"
          }
        >
          <Field
            name="award_winner"
            label="Award Winner"
            icon={<MdPerson />}
            required
            rules={[{ required: true, message: "Please select award winner!" }]}
            helper="Who received the award. Choose from your cast & crew."
            fullWidth
          >
            <Select
              size="large"
              placeholder="Select award winner"
              showSearch
              options={winnerOptions}
            />
          </Field>
        </ConfigProvider>
      </FieldGrid>
    </ModalBody>
  );
};

const ModalBody = styled.div`
  width: 100%;
  padding-top: 0.4rem;

  & .ant-form-item {
    margin-bottom: 0;
  }

  & .ant-form-item-label > label::after {
    display: none;
  }

  & .ant-input,
  & .ant-input-number,
  & .ant-picker,
  & .ant-select-selector,
  & .ant-input-affix-wrapper,
  & .ant-input-number-affix-wrapper {
    border-radius: 0.6rem !important;
    background-color: ${card.innerBg} !important;
    border-color: ${card.border} !important;
    color: ${colors.text.hex} !important;
  }

  & .ant-input::placeholder,
  & .ant-input-number-input::placeholder,
  & .ant-select-selection-placeholder,
  & .ant-picker-input > input::placeholder {
    color: ${colors.text.hex} !important;
    opacity: 0.4 !important;
  }

  & .ant-input:hover,
  & .ant-input-number:hover,
  & .ant-picker:hover,
  & .ant-select:hover .ant-select-selector,
  & .ant-input-affix-wrapper:hover,
  & .ant-input-number-affix-wrapper:hover {
    border-color: ${colors.primary.hex} !important;
  }

  & .ant-input:focus,
  & .ant-input-focused,
  & .ant-input-number-focused,
  & .ant-picker-focused,
  & .ant-select-focused .ant-select-selector,
  & .ant-input-affix-wrapper-focused,
  & .ant-input-number-affix-wrapper-focused {
    border-color: ${colors.primary.hex} !important;
    box-shadow: 0 0 0 2px ${card.accentSoft} !important;
  }

  & .ant-select,
  & .ant-picker,
  & .ant-input,
  & .ant-input-number,
  & .ant-input-affix-wrapper,
  & .ant-input-textarea,
  & .ant-input-number-affix-wrapper {
    width: 100%;
  }

  & .ant-select-selection-item,
  & .ant-picker-input > input,
  & .ant-input-number-input {
    color: ${colors.text.hex} !important;
  }

  & .ant-select-arrow,
  & .ant-picker-suffix {
    color: ${colors.text.hex} !important;
    opacity: 0.55;
  }

  & .ant-form-item-control,
  & .ant-form-item-control-input,
  & .ant-form-item-control-input-content {
    min-width: 0;
  }
`;
